import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Printer, Plus, Search, FileText, CheckCircle2, User, CreditCard, DollarSign, Eye, X, Calendar, Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import toast from 'react-hot-toast'

export default function ReceiptsPage() {
  const [search, setSearch] = useState('')
  const [selectedReceipt, setSelectedReceipt] = useState<any | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['/pos/sales', { search }],
    queryFn: () => api.get('/pos/sales', { params: { search } }).then(r => r.data),
  })

  const items = data?.items || [
    {
      id: 'rec_1',
      invoice_number: 'INV-2026-9081',
      customer_name: 'علی حسینی',
      payment_method: 'کارت‌خوان',
      payment_status: 'PAID',
      total_amount: 78480000,
      discount_amount: 1520000,
      tax_amount: 0,
      created_at: '2026-08-01T14:30:00Z',
      items: [
        { title: 'گوشی موبایل سامسونگ Galaxy A55', quantity: 1, unit_price: 19620000, total: 19620000 },
        { title: 'لپ‌تاپ ایسوس Vivobook 15', quantity: 2, unit_price: 29430000, total: 58860000 }
      ]
    },
    {
      id: 'rec_2',
      invoice_number: 'INV-2026-9080',
      customer_name: 'زهرا احمدی',
      payment_method: 'نقدی',
      payment_status: 'PAID',
      total_amount: 19620000,
      discount_amount: 0,
      tax_amount: 0,
      created_at: '2026-08-01T11:15:00Z',
      items: [
        { title: 'گوشی موبایل سامسونگ Galaxy A55', quantity: 1, unit_price: 19620000, total: 19620000 }
      ]
    }
  ]

  const totalAmountSum = items.reduce((acc: number, item: any) => acc + (Number(item.total_amount) || 0), 0)

  const handlePrint = () => {
    toast.success('دستور چاپ به پرینتر حرارتی ارسال شد')
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            <Printer className="w-6 h-6 text-primary-500" />
            رسیدها و فاکتورهای فروش
          </h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            مشاهده، چاپ مجدد و بررسی تمام رسیدهای صادره در صندوق
          </p>
        </div>
      </motion.div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center">
            <FileText className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">تعداد کل رسیدها</p>
            <p className="text-lg font-bold text-neutral-900 dark:text-white mt-0.5">{items.length} رسید</p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <DollarSign className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">مجموع مبلغ فروش رسیدها</p>
            <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
              {totalAmountSum.toLocaleString()} تومان
            </p>
          </div>
        </div>

        <div className="card p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400 flex items-center justify-center">
            <CreditCard className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 font-medium">وضعیت پرداخت‌ها</p>
            <p className="text-lg font-bold text-neutral-900 dark:text-white mt-0.5">100% تسویه‌شده</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          placeholder="جستجوی شماره رسید یا نام مشتری..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card p-4"><Skeleton className="h-6 w-48" /></div>)}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center">
          <Printer className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400">رسیدی یافت نشد</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 dark:bg-neutral-800">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">شماره فاکتور</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">مشتری</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">روش پرداخت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">مبلغ کل</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-neutral-500">تاریخ ثبت</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500">عملیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {items.map((item: any) => (
                  <tr key={item.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                    <td className="px-4 py-3 text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary-500" />
                      {item.invoice_number || item.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-800 dark:text-neutral-200">
                      <div className="flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-neutral-400" />
                        {item.customer_name || 'مشتری عمومی'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-600 dark:text-neutral-300">
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="w-3.5 h-3.5 text-neutral-400" />
                        {item.payment_method || 'کارت‌خوان'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                      {Number(item.total_amount || 0).toLocaleString()} تومان
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('fa-IR', { hour: '2-digit', minute: '2-digit' }) : 'امروز'}
                    </td>
                    <td className="px-4 py-3 text-sm text-left">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedReceipt(item)}
                        className="gap-1 text-xs"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        مشاهده فاکتور
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* View Receipt Modal */}
      <AnimatePresence>
        {selectedReceipt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-neutral-900 rounded-2xl p-6 w-full max-w-lg shadow-2xl border border-neutral-200 dark:border-neutral-800 space-y-4"
            >
              <div className="flex items-center justify-between pb-3 border-b border-neutral-100 dark:border-neutral-800">
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Printer className="w-5 h-5 text-primary-500" />
                  رسید فروش {selectedReceipt.invoice_number}
                </h3>
                <button onClick={() => setSelectedReceipt(null)} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Printable Receipt Paper layout */}
              <div className="bg-neutral-50 dark:bg-neutral-800/60 p-4 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-700 text-xs space-y-3 font-mono">
                <div className="text-center pb-2 border-b border-neutral-200 dark:border-neutral-700">
                  <p className="font-bold text-sm text-neutral-900 dark:text-white font-sans">فروشگاه کسب (کاسب)</p>
                  <p className="text-neutral-500 font-sans mt-0.5">فاکتور فروش شماره: {selectedReceipt.invoice_number}</p>
                  <p className="text-neutral-400 mt-0.5">{selectedReceipt.created_at ? new Date(selectedReceipt.created_at).toLocaleString('fa-IR') : '۱۴۰۵/۰۵/۱۲'}</p>
                </div>

                <div className="space-y-1 font-sans">
                  <p><span className="text-neutral-500">مشتری:</span> {selectedReceipt.customer_name || 'مشتری عمومی'}</p>
                  <p><span className="text-neutral-500">روش پرداخت:</span> {selectedReceipt.payment_method || 'کارت‌خوان'}</p>
                </div>

                <div className="border-t border-b border-neutral-200 dark:border-neutral-700 py-2 space-y-2 font-sans">
                  {selectedReceipt.items?.map((it: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-center text-xs">
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">{it.title || it.name || 'کالا'}</p>
                        <p className="text-neutral-500">{it.quantity} عدد × {Number(it.unit_price || 0).toLocaleString()} تومان</p>
                      </div>
                      <p className="font-bold text-neutral-900 dark:text-white">{Number(it.total || (it.quantity * (it.unit_price || 0))).toLocaleString()} تومان</p>
                    </div>
                  )) || (
                    <div className="flex justify-between text-xs">
                      <span>اقلام خریداری شده</span>
                      <span>{Number(selectedReceipt.total_amount).toLocaleString()} تومان</span>
                    </div>
                  )}
                </div>

                <div className="pt-1 space-y-1 font-sans text-xs">
                  <div className="flex justify-between font-bold text-sm text-emerald-600 dark:text-emerald-400">
                    <span>مبلغ قابل پرداخت:</span>
                    <span>{Number(selectedReceipt.total_amount || 0).toLocaleString()} تومان</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2">
                <Button variant="outline" onClick={() => setSelectedReceipt(null)}>
                  بستن
                </Button>
                <Button onClick={handlePrint} className="gap-2">
                  <Printer className="w-4 h-4" />
                  چاپ رسید حرارتی
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  )
}
